import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";

/**
 * Reusable hamburger drawer for public marketing pages (Landing, /earn,
 * /r/{slug}). Each caller renders it *once* in the nav bar and passes their
 * own list of link items — external `#anchor` or internal `Link`-like nodes.
 * Also accepts a `cta` React node (usually the primary "Get Started" button)
 * pinned to the bottom of the sheet.
 *
 * Rules:
 *  - Hidden on `md+` so the desktop inline nav remains untouched.
 *  - Locks body scroll while open so the sheet feels app-like on iOS.
 *  - Closes on any link click via `onClose` propagated from parents.
 */
export function PublicNavMobile({ items, cta, accent = "#0D9488" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <div className="md:hidden">
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        data-testid="public-nav-hamburger"
        className="rounded-xl p-2 text-[#1F2937] hover:bg-[#F2ECE1]"
      >
        <Menu size={22} />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <>
          <div
            className="fixed inset-0 z-[100] bg-black/40"
            onClick={() => setOpen(false)}
            data-testid="public-nav-backdrop"
          />
          <aside
            className="fixed inset-y-0 right-0 z-[101] flex w-[85%] max-w-sm flex-col bg-[#FAF7F2] shadow-2xl"
            data-testid="public-nav-drawer"
          >
            <div className="flex items-center justify-between border-b border-[#E5DECF] px-5 py-4">
              <div className="font-heading text-lg font-extrabold text-[#1F2937]">Menu</div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="public-nav-close"
                className="rounded-xl p-1.5 text-[#1F2937] hover:bg-[#F2ECE1]"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-4">
              {items.map((it) => (
                <div key={it.key || it.label} onClick={() => setOpen(false)}>
                  {it.node}
                </div>
              ))}
            </nav>
            {cta && (
              <div className="border-t border-[#E5DECF] p-4" onClick={() => setOpen(false)}>
                {cta}
              </div>
            )}
          </aside>
        </>,
        document.body
      )}
    </div>
  );
}

export default PublicNavMobile;
